import { useState, useRef, useEffect, useCallback } from 'react';
import '../css/customLanguageDropdown.scss';

export type Option = {
  value: string;
  label: React.ReactNode;
};

export interface CustomDropdownProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  renderValue?: (value: string) => React.ReactNode;
  ariaLabel?: string;
}

export default function CustomLanguageDropdown({ options, value, onChange, className = '', renderValue, ariaLabel = 'Select language' }: CustomDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleOptionSelect = useCallback(
    (selectedValue: string) => {
      onChange(selectedValue);
      setIsOpen(false);
    },
    [onChange]
  );

  return (
    <div ref={dropdownRef} className={`dropdown ${className}`}>
      <button type="button" onClick={() => setIsOpen((prev) => !prev)} className="dropdown-toggle" aria-haspopup="true" aria-expanded={isOpen} aria-label={ariaLabel}>
        {renderValue ? renderValue(value) : selectedOption?.label}
      </button>
      {isOpen && (
        <div className="dropdown-menu" role="menu">
          {options.map((option) => (
            <button key={option.value} type="button" onClick={() => handleOptionSelect(option.value)} className={`dropdown-item ${option.value === value ? 'selected' : ''}`} role="menuitem">
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
